import * as React from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { flashSalesIconSize } from '@/app/common/icons/promotionIcons';
import { PeriodicPromotion } from '@/app/common/components/Promotions/LastMinuteDealsBadge/PeriodicPromotion';
import { useIsDesktopOrGreater } from '../hooks/useMediaSSR';
import { getPromotionLabel } from './promotionLabel';

type MealPlanPromotionProps = {
  promotion?: MealPlanPromotion;
  hidePromotion?: boolean;
} & React.HtmlHTMLAttributes<HTMLElement>;

const DiscountNameBadge = styled(Text).attrs({ variant: 'BodySmall' })<{
  $isBlackFriday: boolean;
  $isFlashSale?: boolean;
  $isLmnDeal?: boolean;
  $isDesktop?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${(p) =>
    p.$isBlackFriday
      ? p.theme.commons.colors.gray800
      : p.$isLmnDeal
        ? p.theme.commons.colors.primary50
        : p.$isFlashSale
          ? p.theme.commons.colors.fsPrimary
          : p.theme.commons.colors.primary50};
  color: ${(p) => (p.$isBlackFriday || p.$isFlashSale ? p.theme.commons.colors.gray0 : p.theme.commons.colors.primary)};
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 4px;
`;

export default function MealPlanPromotion(props: MealPlanPromotionProps) {
  const { promotion, hidePromotion = false } = props;
  const { commonData, localization } = usePageWrapperData();
  const { t } = useTranslation();

  const showBlackFridayInOffers = false;
  const isDesktop = useIsDesktopOrGreater(commonData.isDesktopDevice);

  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals === true;
  const isLmnDeal = showLastMinuteDeals && promotion?.type?.toUpperCase() === 'FLASH_SALE';
  const isFlashSaleDeal = !showLastMinuteDeals && promotion?.type?.toUpperCase() === 'FLASH_SALE';
  const { imageBasePath } = localization;

  return (
    <>
      {!hidePromotion && showBlackFridayInOffers && (
        <DiscountNameBadge $isBlackFriday={true} $isDesktop={isDesktop}>
          {t('vacation.offer.blackfriday')}
        </DiscountNameBadge>
      )}

      {!hidePromotion &&
        promotion?.label &&
        !(showBlackFridayInOffers && promotion?.type?.toUpperCase() === 'BLACK_FRIDAY_OFFER') && (
          <BadgeContainer>
            <DiscountNameBadge
              data-testid="room-savings"
              $isBlackFriday={promotion?.type?.toUpperCase() === 'BLACK_FRIDAY_OFFER'}
              $isFlashSale={isFlashSaleDeal}
              $isLmnDeal={isLmnDeal}
              $isDesktop={isDesktop}
            >
              {isLmnDeal ? (
                <img
                  src={`${imageBasePath}lm_deals.svg`}
                  alt="last-minute-icon-logo"
                  css={css`
                    width: 14px;
                    height: 14px;
                    /* TODO USE SVG ICONS */
                    filter: brightness(0) saturate(100%) invert(12%) sepia(83%) saturate(6854%) hue-rotate(322deg)
                      brightness(96%) contrast(100%);
                  `}
                />
              ) : isFlashSaleDeal ? (
                <Icon
                  category="Brands and products"
                  name="icon-flash-sales"
                  size={flashSalesIconSize(16)}
                  footprint={16}
                  color="iconOnDark"
                  alt="lastminute deal"
                />
              ) : undefined}
              {isLmnDeal
                ? t('vacation.select.details.lmdeal.badge_label') || 'Last Minute deals'
                : getPromotionLabel(promotion, t)}
            </DiscountNameBadge>
            <PeriodicPromotion />
          </BadgeContainer>
        )}
    </>
  );
}
